// Unified kiki composer. Laid out like a preview of the KikiPage: the host's
// profile row up top, the note underneath, then an "Event section" describing
// what the kiki is for.
//
//   • Event-tied (from an event page): the Event section previews the locked
//     event and submitting creates a buddy request for it.
//   • Standalone (from the events page): the Event section is the custom-plan
//     builder (category → title/location/date/city) and submitting posts a
//     standalone kiki.
//
// One dialog, used in both places.
import { useEffect, useState } from "react";
import {
  X,
  CalendarDays,
  Clock,
  MapPin,
  Globe,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useProfile } from "../lib/api/useProfile";
import { useAuth } from "../lib/platform";
import { useCreateBuddyRequest, useUpdateBuddyRequest } from "../lib/api/useEventBuddies";
import { KIKI_CATEGORIES, KIKI_CUSTOM_EMOJIS, getKikiCategory } from "../lib/kikiTemplates";
import { useSearchCities } from "../lib/api/useCities";
import { formatTimeToDisplay } from "../utils/dateUtils";
import { CitySelectModal, type SelectedCity } from "./CitySelectModal";
import { EventPoster } from "./EventPoster";
import { DateBadge } from "./ux/DateBadge";
import { ProfileImage } from "./ProfileImage";
import { PublicProfileButton } from "./PublicProfileModal";
import { Sheet } from "./ux/Sheet";
import { useToast } from "./ux/Toast";
import tracking from "../lib/tracking";

export interface ComposerEvent {
  slug: string;
  name: string;
  date?: string | null;
  time?: string | null;
  coverImageUrl?: string | null;
  venueName?: string | null;
  venueCity?: string | null;
  types?: string[];
  posterLabel?: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Standalone (events page): city to attach a custom kiki to. */
  cityId?: string | null;
  /** Standalone: jump straight into a category (from an inline template swatch). */
  initialCategoryId?: string;
  /** Event-tied (event page): locks the Event section to this event. */
  event?: ComposerEvent | null;
  /** Edit an existing event-tied kiki (note only). */
  editing?: { id: string; note: string | null } | null;
}

// Compact, read-only preview of the event a kiki is tied to — mirrors the
// KikiPage event card so the composer feels like a preview of the result.
function EventPreview({ event }: { event: ComposerEvent }) {
  const timeLabel = event.time ? formatTimeToDisplay(event.time) : null;
  const venueLine = [event.venueName, event.venueCity].filter(Boolean).join(" · ") || null;
  return (
    <div className="rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden">
      <div className="relative aspect-[16/9] bg-neutral-900">
        {event.coverImageUrl ? (
          <img src={event.coverImageUrl} alt={event.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <EventPoster
            name={event.name}
            label={event.posterLabel ?? ""}
            types={event.types ?? []}
            className="absolute inset-0 w-full h-full"
          />
        )}
      </div>
      <div className="p-3 flex items-center gap-3">
        {event.date && <DateBadge date={event.date} size="md" />}
        <div className="min-w-0">
          <p className="font-semibold text-sm leading-tight truncate">{event.name}</p>
          {(venueLine || timeLabel) && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
              {[venueLine, timeLabel].filter(Boolean).join(" · ")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function CreateKikiModal({ isOpen, onClose, cityId, initialCategoryId, event, editing }: Props) {
  const eventMode = !!event;
  const isEdit = !!editing;
  const toast = useToast();
  const queryClient = useQueryClient();

  // Host preview row.
  const { data: profileData } = useProfile();
  const { user: authUser, userId } = useAuth();
  const u = profileData?.success
    ? (profileData.data?.user as
        | { firstName?: string; lastName?: string | null; profileImageUrl?: string | null }
        | undefined)
    : undefined;
  const previewUser = {
    firstName: u?.firstName ?? authUser?.firstName ?? null,
    lastName: u?.lastName ?? authUser?.lastName ?? null,
    profileImageUrl: u?.profileImageUrl,
  };

  const [note, setNote] = useState("");

  // Standalone plan-builder state.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [customEmoji, setCustomEmoji] = useState(KIKI_CUSTOM_EMOJIS[0]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [selectedCity, setSelectedCity] = useState<SelectedCity | null>(null);
  const [cityPickerOpen, setCityPickerOpen] = useState(false);

  // Event-tied mutations (hooks must run unconditionally; harmless when standalone).
  const createBuddy = useCreateBuddyRequest(event?.slug ?? "");
  const updateBuddy = useUpdateBuddyRequest(event?.slug ?? "");

  const { data: citiesData } = useSearchCities("", cityId ?? undefined, 50, false);

  const resetFields = () => {
    setTitle("");
    setLocation("");
    setCustomEmoji(KIKI_CUSTOM_EMOJIS[0]);
    setDate("");
    setTime("");
  };

  useEffect(() => {
    if (!isOpen) return;
    tracking.kikiCreateModalOpened(eventMode ? "event_tied" : "standalone");
    setNote(editing?.note ?? "");
    setSelectedId(initialCategoryId ?? null);
    resetFields();
    setSelectedCity(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialCategoryId, editing]);

  // Pre-select the current city for standalone kikis.
  useEffect(() => {
    if (!isOpen || eventMode || !cityId || selectedCity) return;
    const list = citiesData?.success ? citiesData.data ?? [] : [];
    const match = list.find((c) => c.id === cityId);
    if (match) setSelectedCity({ id: match.id, name: match.name, emoji: match.emoji });
  }, [isOpen, eventMode, cityId, citiesData, selectedCity]);

  const category = getKikiCategory(selectedId);
  const hasTitleField = !!category?.fields.some((f) => f.key === "title");
  const resolvedName = category ? (hasTitleField ? title.trim() : category.label) : "";
  const resolvedEmoji = category ? (category.custom ? customEmoji : category.emoji) : "";

  const createStandalone = useMutation({
    mutationFn: () =>
      api.post("/events/kikis", {
        activityInfo: {
          name: resolvedName,
          emoji: resolvedEmoji,
          ...(location.trim() ? { location: location.trim() } : {}),
          ...(date ? { date } : {}),
          ...(time ? { time } : {}),
        },
        note: note.trim() || undefined,
        cityId: selectedCity?.id ?? cityId ?? undefined,
      }),
    onSuccess: () => {
      tracking.kikiCreateSubmitted("standalone", !!note.trim());
      queryClient.invalidateQueries({ queryKey: ["eventsList"] });
      onClose();
    },
    onError: (err) => {
      tracking.kikiCreateError("standalone", (err as Error)?.message ?? "unknown");
    },
  });

  const pending = createBuddy.isPending || updateBuddy.isPending || createStandalone.isPending;
  const canSubmit = eventMode
    ? !pending
    : !!category && resolvedName.length > 0 && !pending;

  const handleSubmit = async () => {
    if (eventMode) {
      const noteVal = note.trim() || undefined;
      try {
        const res = isEdit
          ? await updateBuddy.mutateAsync({ id: editing!.id, note: noteVal })
          : await createBuddy.mutateAsync({ note: noteVal });
        if (res.success) {
          if (isEdit) tracking.kikiUpdated(editing!.id);
          else tracking.kikiCreateSubmitted("event_tied", !!noteVal);
          toast.success(isEdit ? "Updated" : "Posted");
          onClose();
        } else {
          tracking.kikiCreateError("event_tied", (res as { error?: string }).error || "unknown");
          toast.error((res as { error?: string }).error || "Couldn't save");
        }
      } catch (err) {
        tracking.kikiCreateError("event_tied", (err as Error).message || "unknown");
        toast.error((err as Error).message || "Couldn't save");
      }
      return;
    }
    createStandalone.mutate();
  };

  const previewName = previewUser.firstName ?? "You";
  const title2 = isEdit ? "Edit your kiki" : "Start a kiki";

  return (
    <>
      <Sheet open={isOpen} onClose={onClose} hideHeader>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            <h2 className="flex-1 text-lg font-bold text-neutral-900 dark:text-white">{title2}</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <X className="w-4 h-4 text-neutral-500" />
            </button>
          </div>

          {/* Host profile row — opens the host's public profile */}
          {userId ? (
            <PublicProfileButton
              userId={userId}
              ariaLabel="View your profile"
              className="flex items-center gap-3 w-full text-left"
            >
              <ProfileImage size="md" user={previewUser} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                  {previewName}
                  <span className="font-normal text-neutral-400 dark:text-neutral-500"> · you</span>
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Hosting this kiki</p>
              </div>
            </PublicProfileButton>
          ) : (
            <div className="flex items-center gap-3">
              <ProfileImage size="md" user={previewUser} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                  {previewName}
                  <span className="font-normal text-neutral-400 dark:text-neutral-500"> · you</span>
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Hosting this kiki</p>
              </div>
            </div>
          )}

          {/* Note */}
          <textarea
            placeholder="Add a note: the vibe, who you're hoping to meet…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={3}
            className="w-full px-3 py-2 rounded-xl border border-base-300 dark:border-neutral-700 bg-base-100 dark:bg-neutral-800 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 resize-none"
          />

          {/* Event section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
              {eventMode ? "The event" : "What's the plan?"}
            </p>

            {eventMode ? (
              <EventPreview event={event!} />
            ) : !category ? (
              /* Category picker */
              <div className="grid grid-cols-4 gap-x-2 gap-y-4 pt-1">
                {KIKI_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className="flex flex-col items-center gap-1.5 focus:outline-none group/card"
                  >
                    <div
                      className={`w-14 h-14 rounded-full bg-gradient-to-br ${c.gradient} flex items-center justify-center text-2xl shadow-md transition-transform duration-200 group-hover/card:scale-105`}
                    >
                      <span style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.35))" }}>{c.emoji}</span>
                    </div>
                    <span className="text-xs font-medium text-center leading-tight text-neutral-600 dark:text-neutral-300">
                      {c.label}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              /* Category form */
              <div className="space-y-3">
                {/* Tapping the chosen type reopens the type selector. */}
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="w-full flex items-center gap-3 text-left"
                >
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${category.gradient} flex items-center justify-center text-lg shrink-0`}>
                    <span style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}>{resolvedEmoji}</span>
                  </div>
                  <p className="flex-1 min-w-0 text-sm font-semibold text-neutral-900 dark:text-white leading-tight">
                    {category.prompt}
                  </p>
                  <span className="shrink-0 text-xs font-medium text-primary-500">Change</span>
                  <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
                </button>

                {category.custom && (
                  <div className="flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {KIKI_CUSTOM_EMOJIS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => setCustomEmoji(e)}
                        className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-colors ${
                          customEmoji === e
                            ? "bg-primary-500/25 ring-2 ring-primary-500"
                            : "bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                        }`}
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                )}

                {category.fields.map((f) => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                      {f.label}
                    </label>
                    {f.key === "location" ? (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-base-300 dark:border-neutral-700 bg-base-100 dark:bg-neutral-800 focus-within:ring-2 focus-within:ring-primary-500/40">
                        <MapPin className="w-4 h-4 text-neutral-400 shrink-0" />
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          placeholder={f.placeholder}
                          maxLength={120}
                          className="w-full min-w-0 bg-transparent text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none"
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={f.placeholder}
                        maxLength={100}
                        className="w-full px-3 py-2 rounded-xl border border-base-300 dark:border-neutral-700 bg-base-100 dark:bg-neutral-800 text-sm text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                      />
                    )}
                  </div>
                ))}

                {/* City */}
                <div>
                  <label className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">City</label>
                  <button
                    type="button"
                    onClick={() => setCityPickerOpen(true)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-base-300 dark:border-neutral-700 bg-base-100 dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                  >
                    <Globe className="w-4 h-4 text-neutral-400 shrink-0" />
                    {selectedCity ? (
                      <span className="flex items-center gap-1.5 min-w-0 text-neutral-900 dark:text-white">
                        <span>{selectedCity.emoji}</span>
                        <span className="truncate">{selectedCity.name}</span>
                      </span>
                    ) : (
                      <span className="text-neutral-400">Choose a city</span>
                    )}
                    <ChevronRight className="w-4 h-4 text-neutral-400 ml-auto shrink-0" />
                  </button>
                </div>

                {/* When */}
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-base-300 dark:border-neutral-700 bg-base-100 dark:bg-neutral-800 focus-within:ring-2 focus-within:ring-primary-500/40">
                    <CalendarDays className="w-4 h-4 text-neutral-400 shrink-0" />
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full min-w-0 bg-transparent text-sm text-neutral-900 dark:text-white focus:outline-none [color-scheme:light] dark:[color-scheme:dark]"
                    />
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-base-300 dark:border-neutral-700 bg-base-100 dark:bg-neutral-800 focus-within:ring-2 focus-within:ring-primary-500/40">
                    <Clock className="w-4 h-4 text-neutral-400 shrink-0" />
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="w-full min-w-0 bg-transparent text-sm text-neutral-900 dark:text-white focus:outline-none [color-scheme:light] dark:[color-scheme:dark]"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
          >
            {pending && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save" : "Post kiki"}
          </button>

          {(createBuddy.isError || updateBuddy.isError || createStandalone.isError) && (
            <p className="text-xs text-center text-red-500">Something went wrong. Try again.</p>
          )}
        </div>
      </Sheet>

      <CitySelectModal
        isOpen={cityPickerOpen}
        onClose={() => setCityPickerOpen(false)}
        selectedId={selectedCity?.id}
        onSelect={(city) => setSelectedCity(city)}
        hideComingSoon
      />
    </>
  );
}
